import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";

import type { SxProps, Theme } from "@mui/material/styles";

import { useSite } from "@/api/zero/sites";
import { CloseRoundedIcon, LocalFireDepartmentRoundedIcon } from "@/icons";

interface BroadcastWarmupBannerProps {
  siteId: string;
  /**
   * Drop the page-level gutters (px/pt) so the banner can be embedded inside an
   * already-padded container — e.g. the broadcast composer's left column — and
   * align with its content. A bottom margin still separates it from the content
   * below. Defaults to the full-width, page-level spacing used on the listing.
   */
  disableGutters?: boolean;
  /**
   * Copy shown after the bold "Your send domain is warming up." lead. Defaults
   * to the broadcast-specific "keep broadcasts small" framing; surfaces for
   * triggered/automated sends (e.g. Dragon Tiles, FOG-2319) pass their own.
   */
  description?: string;
  /**
   * Overrides on the outer spacing Box — e.g. to match a host page's own
   * gutter. Lives on the component (not a wrapper) so no spacing lingers when
   * the banner is hidden or dismissed.
   */
  sx?: SxProps<Theme>;
}

/**
 * Soft, educational guardrail shown on the Broadcast listing page while the
 * brand's send domain is warming up (FOG-1750). Awareness-only — it never
 * blocks sending. Dismissal is component-local (no persistence) so the banner
 * re-surfaces on the next page visit, per the acceptance criteria, and it
 * disappears automatically once staff turn the site's warmup notification off
 * (Settings › Email, `broadcast_warmup_notification_enabled`). While the site
 * is loading or unreadable the banner stays hidden (safe default).
 */
export function BroadcastWarmupBanner({
  siteId,
  disableGutters = false,
  description = "For best deliverability, keep broadcasts small and send to your most engaged subscribers only.",
  sx,
}: BroadcastWarmupBannerProps) {
  const { data: site } = useSite(siteId);
  const isWarming = site?.broadcast_warmup_notification_enabled ?? false;
  const [dismissed, setDismissed] = useState(false);

  if (!isWarming || dismissed) {
    return null;
  }

  return (
    <Box
      sx={[
        disableGutters ? { mb: 4 } : { px: 8, pt: 7 },
        ...((Array.isArray(sx) ? sx : [sx]) as SxProps<Theme>[]),
      ]}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={4}
        data-testid="broadcast-warmup-banner"
        role="status"
        sx={(theme) => {
          const states = theme.palette.primary.customStateColor;
          return {
            p: 5,
            backgroundColor: states?.selected,
            border: `1px solid ${states?.focusVisible ?? "transparent"}`,
          };
        }}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: theme.spacing(8),
            height: theme.spacing(8),
            borderRadius: theme.shape.borderRadiusMd,
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.main,
          })}
        >
          <LocalFireDepartmentRoundedIcon fontSize="small" />
        </Box>
        <Typography
          variant="body2"
          sx={(theme) => ({ flexGrow: 1, color: theme.palette.text.primary })}
        >
          <Box
            component="span"
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightScale.semiBold,
            })}
          >
            Your send domain is warming up.
          </Box>{" "}
          {description}
        </Typography>
        <IconButton
          aria-label="Close"
          size="small"
          onClick={() => setDismissed(true)}
          sx={(theme) => ({
            flexShrink: 0,
            alignSelf: "flex-start",
            color: theme.palette.text.secondary,
          })}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
